import { z } from "zod";
import { HouseholdRole } from "../domain.js";
import { Id, IsoTimestamp } from "../primitives.js";

export const Household = z.object({
  id: Id,
  name: z.string(),
  currency: z.string(),
  timezone: z.string(),
  role: HouseholdRole,
});

export const HouseholdCreate = z.object({
  name: z.string().trim().min(1).max(60),
});

export const HouseholdPatch = z
  .object({
    name: z.string().trim().min(1).max(60),
    timezone: z.string().min(1).max(60),
  })
  .partial()
  .refine((patch) => Object.keys(patch).length > 0, "Nothing to change.");

export const Member = z.object({
  userId: Id,
  name: z.string(),
  email: z.string(),
  role: HouseholdRole,
  joinedAt: IsoTimestamp,
});

export const MemberList = z.object({ data: z.array(Member) });

export const InviteCreate = z.object({
  email: z.email("That does not look like an email address"),
  role: HouseholdRole.default("member"),
});

export const Invite = z.object({
  id: Id,
  email: z.string(),
  role: HouseholdRole,
  expiresAt: IsoTimestamp,
  acceptedAt: IsoTimestamp.nullable(),
  createdAt: IsoTimestamp,
});

export const InviteList = z.object({ data: z.array(Invite) });

/**
 * The link is returned **once**, to the owner who just created it, and is
 * never readable again — only its hash is stored.
 *
 * That is a deliberate softening of "the raw token only exists in the email":
 * transactional email can be delayed or filtered, and an invite nobody can
 * deliver is not a feature. Showing it to the person who chose the recipient
 * and already holds every permission in the household leaks nothing new.
 */
export const InviteCreated = z.object({
  invite: Invite,
  acceptUrl: z.url(),
  emailed: z.boolean(),
});

export const InviteAcceptResult = z.object({
  householdId: Id,
  name: z.string(),
  role: HouseholdRole,
});

export type Household = z.infer<typeof Household>;
export type HouseholdCreate = z.infer<typeof HouseholdCreate>;
export type HouseholdPatch = z.infer<typeof HouseholdPatch>;
export type Member = z.infer<typeof Member>;
export type MemberList = z.infer<typeof MemberList>;
export type InviteCreate = z.infer<typeof InviteCreate>;
export type Invite = z.infer<typeof Invite>;
export type InviteList = z.infer<typeof InviteList>;
export type InviteCreated = z.infer<typeof InviteCreated>;
export type InviteAcceptResult = z.infer<typeof InviteAcceptResult>;
